// @ts-strict-ignore
import * as React from 'react';

import type {
  CustomFeedActions,
  CustomFeedState,
} from '@/features/CustomFeedV2/hooks';
import { useCustomFeedState } from '@/features/CustomFeedV2/hooks';

export const CustomFeedContext =
  React.createContext<CustomFeedContextType | null>(null);

export const CustomFeedDispatchContext =
  React.createContext<React.Dispatch<CustomFeedActions> | null>(null);

export const useCustomFeedContext = (): CustomFeedContextType => {
  const value = React.useContext(CustomFeedContext);

  if (!value) {
    throw new Error(
      'useCustomFeedContext should be used inside CustomFeedContextProvider'
    );
  }

  return value;
};

export const useCustomFeedContextDispatch =
  (): React.Dispatch<CustomFeedActions> => {
    const context = React.useContext(CustomFeedDispatchContext);

    if (!context) {
      throw new Error(
        'useCustomFeedContextDispatch must be used inside CustomFeedContextProvider'
      );
    }

    return context;
  };

export const CustomFeedContextProvider: React.FunctionComponent<
  CustomFeedContextProviderType
> = ({
  initialValue = {
    board: {
      name: '',
      description: '',
      keywords: {
        include: [],
        exclude: [],
      },
      sources: {},
      secondary_sources: {},
      common: {},
      date_range: '',
      group: 'default',
    },
    aiSmarts: {
      isOptionsPage: true,
      currentTopic: '',
    },
  },
  children,
}) => {
  const [data, dispatch] = useCustomFeedState({
    board: initialValue.board,
    aiSmarts: initialValue.aiSmarts,
    activeKeywordType: 'all',
  });

  return (
    <CustomFeedContext.Provider value={React.useMemo(() => ({ data }), [data])}>
      <CustomFeedDispatchContext.Provider value={dispatch}>
        {children}
      </CustomFeedDispatchContext.Provider>
    </CustomFeedContext.Provider>
  );
};

export type CustomFeedContextType = {
  data: CustomFeedState;
};

type CustomFeedContextProviderType = Record<string, unknown> & {
  initialValue?: CustomFeedState;
};
